import type { FC, ReactNode } from "react";
import type {
	ClientPortfolioReportingSummaryResponseDTO,
	PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataLineChart } from "web_myplann/Portfolio/Components/DataLineChart/DataLineChart";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import type { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { getPortfolioValueDate } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

type Props = {
	baseData: PortfolioDetailResponseDTO;
	summary: ClientPortfolioReportingSummaryResponseDTO | null | undefined;
	graphData: ReturnType<typeof calculateGraphData>;
	currencySign: string;
	extraRows?: ReactNode;
	alwaysShowChartInfo?: boolean;
};

export const ValueChartSection: FC<Props> = ({
	baseData,
	summary,
	graphData,
	currencySign,
	extraRows,
	alwaysShowChartInfo = false,
}) => {
	const { t } = useResource();
	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		graphData;

	return (
		<>
			<DataLineChart
				hideDivider
				totalValue={totalValue}
				totalEvaluationValue={summary?.totalEvaluationValue ?? 0}
				totalEvaluationPercent={summary?.totalEvaluationPercent ?? 0}
				daysData={daysData}
				currencySign={currencySign}
				toDate={toDate}
				showChartInfo={alwaysShowChartInfo || showChartInfo}
			/>

			{showChartInfo && (
				<>
					{extraRows}

					<DataRow
						label={t(Resources.Portfolio.Chart.InvestedTogether)}
						value={moneyFormatter(totalInvestedValue, currencySign, "", 2)}
					/>

					<DataRow
						label={t(Resources.Common.ToDate)}
						value={getPortfolioValueDate(daysData, baseData)}
					/>
				</>
			)}

			{daysData.length !== 0 && <SectionDivider />}
		</>
	);
};
